// Lib
import React, { ReactElement, useEffect } from 'react';
import classNames from 'classnames';
import { defer } from 'lodash';
import { useSelector } from 'react-redux';

// Components
import DocumentModalHeader from './DocumentModalHeader';
import DocumentModalSidebar from './DocumentModalSidebar';
import DocumentModalTiptapEditor from './DocumentModalTiptapEditor';
import AttachedCommentsContainer from '../../attachedComment/AttachedCommentsContainer';

// Hooks
import useTipTapDocumentEditingProps from './useTipTapDocumentEditingProps';
import { useAttachedCommentStyles } from '../../attachedComment/useAttachedCommentStyles';

// Constants
import { DOCUMENT_MODAL_TITLE_EDITOR_KEY } from './documentModalConstants';

// Types
import { ImMNElement } from '../../../../common/elements/elementModelTypes';
import { TiptapContent } from '../../../../common/tiptap/tiptapTypes';

// Selectors
import getGridSize from '../../../utils/grid/gridSizeSelector';
import { getCurrentBoardId } from '../../selectors/currentBoardSelector';
import { getPlatformDetailsSelector } from '../../../platform/platformSelector';

// Utils
import doesEditorJsonHaveText from '../../../../common/tiptap/utils/jsonContentUtils/doesEditorJsonHaveText';
import { isPlatformTablet } from '../../../platform/utils/platformDetailsUtils';
import { isLocationAttachedInline } from '../../../../common/elements/utils/elementLocationUtils';

// Styles
import './DocumentModalTiptap.scss';

export interface DocumentModalTiptapProps {
    elementId: string;
    element: ImMNElement;
    textContent: TiptapContent;

    permissions: number;

    isEditable: boolean;
    spellCheck: boolean;

    isModalView: boolean;
    isPreview: boolean;

    elementInstanceModalRef: (elem: HTMLDivElement | null) => void;
}

const DocumentModalTiptap = (props: DocumentModalTiptapProps): ReactElement => {
    const { element, elementId, textContent, isPreview, isEditable, elementInstanceModalRef, permissions } = props;

    const gridSize = useSelector(getGridSize);
    const currentBoardId = useSelector(getCurrentBoardId);

    const platformDetails = useSelector(getPlatformDetailsSelector);

    const tipTapDocumentEditingProps = useTipTapDocumentEditingProps(elementId, element);
    const { editorKey, isEditing, currentEditorKey, dispatchStartEditingDocument } = tipTapDocumentEditingProps;

    const classes = classNames('DocumentModal DocumentModalTiptap', { preview: isPreview, 'read-only': !isEditable });

    // Delay the start editing action - to allow the title editing to end before switching to
    // the main editor, otherwise the finish editing action would apply to the main content too
    const delayedStartEditingMainContent = () => {
        defer(dispatchStartEditingDocument);
    };

    // On opening up the document modal, automatically start editing the document,
    // unless if the document has content and we're on tablet.
    // This is because we don't want the content to be initially covered by the virtual keyboard.
    useEffect(() => {
        if (!isEditable) return;

        const documentHasContent = !!textContent && doesEditorJsonHaveText(textContent);
        if (isPlatformTablet(platformDetails) && documentHasContent) return;

        delayedStartEditingMainContent();
    }, []);

    // If attached comments have custom colours, we use CSS properties to apply them to the inline comment marks
    const attachedCommentStyles = useAttachedCommentStyles(elementId);

    return (
        <div className={classes} ref={elementInstanceModalRef} style={attachedCommentStyles}>
            {!isPreview && (
                <DocumentModalHeader
                    element={element}
                    elementId={elementId}
                    editorKey={DOCUMENT_MODAL_TITLE_EDITOR_KEY}
                    isEditable={isEditable}
                    onStopEditing={delayedStartEditingMainContent}
                />
            )}
            <div className="document-modal-body">
                {!isPreview && (
                    <DocumentModalSidebar
                        elementId={elementId}
                        element={element}
                        isEditable={isEditable}
                        isEditingDocument={isEditing && currentEditorKey === editorKey}
                        dispatchStartEditingDocument={dispatchStartEditingDocument}
                        permissions={props.permissions}
                    />
                )}
                <div id="document-modal-content" className="content">
                    <DocumentModalTiptapEditor
                        elementId={elementId}
                        textContent={props.textContent}
                        isEditable={isEditable}
                        {...tipTapDocumentEditingProps}
                    />
                </div>
                <div id="document-modal-side" />
                <AttachedCommentsContainer
                    gridSize={gridSize}
                    elementId={elementId}
                    element={element}
                    isPresentational={false}
                    currentBoardId={currentBoardId}
                    permissions={permissions}
                    elementFilterFunction={isLocationAttachedInline}
                    popupCssSelectorPrefix="#document-modal-content "
                />
            </div>
        </div>
    );
};

export default DocumentModalTiptap;
/*
 */
